import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';

const token = localStorage.getItem('token');

// Get aggregate data for specific symbol and timeframe
export const getAggregateData = createAsyncThunk(
    'aggregate/getAggregateData',
    async ({ symbol, timeframe = 'day' }, { rejectWithValue }) => {
        try {
            const response = await axios.get('/api/aggregate/data', {
                params: { symbol, timeframe },
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response.data);
        }
    }
);

// Get available symbols and timeframes
export const getAvailableSymbolsAndTimeframes = createAsyncThunk(
    'aggregate/getAvailableSymbolsAndTimeframes',
    async (_, { rejectWithValue }) => {
        try {
            const response = await axios.get('/api/aggregate/symbols', {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response.data);
        }
    }
);

// Trigger manual data fetch
export const triggerDataFetch = createAsyncThunk(
    'aggregate/triggerDataFetch',
    async (_, { rejectWithValue }) => {
        try {
            const response = await axios.post('/api/aggregate/fetch', {}, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response.data);
        }
    }
);

const initialState = {
    aggregateData: null,
    availableSymbols: [],
    availableTimeframes: [],
    timeframeDetails: {},
    selectedSymbol: 'USDINR',
    selectedTimeframe: 'day',
    loading: false,
    error: null,
    responseTime: null,
    dataAge: null,
    recordCount: 0,
    dateRange: null
};

const aggregateSlice = createSlice({
    name: 'aggregate',
    initialState,
    reducers: {
        setSelectedSymbol: (state, action) => {
            state.selectedSymbol = action.payload;
        },
        setSelectedTimeframe: (state, action) => {
            state.selectedTimeframe = action.payload;
        },
        clearError: (state) => {
            state.error = null;
        },
        resetAggregateState: (state) => {
            return { ...initialState, availableSymbols: state.availableSymbols, availableTimeframes: state.availableTimeframes };
        }
    },
    extraReducers: (builder) => {
        builder
            // Handle getAggregateData
            .addCase(getAggregateData.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(getAggregateData.fulfilled, (state, action) => {
                state.loading = false;
                state.aggregateData = action.payload.data;
                state.responseTime = action.payload.responseTimeMs;
                state.dataAge = action.payload.dataAgeSeconds;
                state.recordCount = action.payload.recordCount;
                state.dateRange = action.payload.dateRange;
                state.error = null;
            })
            .addCase(getAggregateData.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload?.error || 'Failed to fetch aggregate data';
            })
            
            // Handle getAvailableSymbolsAndTimeframes
            .addCase(getAvailableSymbolsAndTimeframes.fulfilled, (state, action) => {
                state.availableSymbols = action.payload.symbols;
                state.availableTimeframes = action.payload.timeframes;
                state.timeframeDetails = action.payload.timeframeDetails;
            })
            
            // Handle triggerDataFetch
            .addCase(triggerDataFetch.fulfilled, (state, action) => {
                console.log('Data fetch triggered:', action.payload.message);
            });
    }
});

export const { setSelectedSymbol, setSelectedTimeframe, clearError, resetAggregateState } = aggregateSlice.actions;

// Selectors
export const selectAggregateData = (state) => state.aggregate.aggregateData;
export const selectAvailableSymbols = (state) => state.aggregate.availableSymbols;
export const selectAvailableTimeframes = (state) => state.aggregate.availableTimeframes;
export const selectSelectedSymbol = (state) => state.aggregate.selectedSymbol;
export const selectSelectedTimeframe = (state) => state.aggregate.selectedTimeframe;
export const selectAggregateLoading = (state) => state.aggregate.loading;
export const selectAggregateError = (state) => state.aggregate.error;
export const selectResponseTime = (state) => state.aggregate.responseTime;
export const selectDataAge = (state) => state.aggregate.dataAge;
export const selectRecordCount = (state) => state.aggregate.recordCount;
export const selectDateRange = (state) => state.aggregate.dateRange;

export default aggregateSlice.reducer;